import type { PrinterView } from 'shared-models/browser';

export const FIXED_PRINTER_STORAGE_KEY = 'posFixedPrinterId';

// Reads the dedicated fixed-printer key only; the manager flow is the sole writer/clearer.
export function getFixedPrinterIdString(): string | null {
  return localStorage.getItem(FIXED_PRINTER_STORAGE_KEY);
}

export function getFixedPrinterId(): number | null {
  const raw = getFixedPrinterIdString();
  if (!raw) return null;
  const parsed = Number(raw);
  return Number.isFinite(parsed) ? parsed : null;
}

export function setFixedPrinterId(printerId: number): void {
  localStorage.setItem(FIXED_PRINTER_STORAGE_KEY, String(printerId));
}

export function clearFixedPrinterId(): void {
  localStorage.removeItem(FIXED_PRINTER_STORAGE_KEY);
}

// Returns the stored id only if still an active cash-drawer printer, else null (stale guard).
export function resolveFixedPrinter(activePrinters: PrinterView[]): number | null {
  const id = getFixedPrinterId();
  if (id == null) return null;
  const match = activePrinters.find((p) => p.id === id && p.active && p.cashDrawer);
  return match ? id : null;
}

export type PrintTarget =
  | { kind: 'print'; printerId: number }
  | { kind: 'pick'; candidates?: PrinterView[] }
  | { kind: 'none' };

// Shared print-routing ladder: fixed wins, else sole active prints, 2+ picks, 0 = none.
export function resolvePrintTarget(activePrinters: PrinterView[], fixedPrinterId: number | null): PrintTarget {
  if (fixedPrinterId != null) return { kind: 'print', printerId: fixedPrinterId };
  if (activePrinters.length === 1) return { kind: 'print', printerId: activePrinters[0].id };
  if (activePrinters.length > 1) return { kind: 'pick' };
  return { kind: 'none' };
}

// The usable billable printers among the active list (list is already active && !human_disabled).
export function billablePrinters(activePrinters: PrinterView[]): PrinterView[] {
  return activePrinters.filter((p) => p.billable);
}

// Receipt routing: billable supersedes the pin. 1 prints, 2+ picks billables, 0 = old ladder.
export function resolveReceiptPrintTarget(activePrinters: PrinterView[]): PrintTarget {
  const billables = billablePrinters(activePrinters);
  if (billables.length === 1) return { kind: 'print', printerId: billables[0].id };
  if (billables.length >= 2) return { kind: 'pick', candidates: billables };
  return resolvePrintTarget(activePrinters, resolveFixedPrinter(activePrinters));
}

// Sole auto-target id for gates/silent-print seeds; null when a pick is needed or nothing routes.
export function resolveReceiptAutoPrinterId(activePrinters: PrinterView[]): number | null {
  const target = resolveReceiptPrintTarget(activePrinters);
  return target.kind === 'print' ? target.printerId : null;
}

// Drawer kick: sole billable cash-drawer printer wins; 2+ = null (prompt); 0 = device drawer.
export function resolveBillableDrawerId(activePrinters: PrinterView[]): number | null {
  const billableDrawers = activePrinters.filter((p) => p.billable && p.cashDrawer);
  if (billableDrawers.length === 1) return billableDrawers[0].id;
  if (billableDrawers.length >= 2) return null;
  return resolveFixedPrinter(activePrinters);
}
